/**
 * The map: a canvas sized to its container (device-pixel sharp), redrawn on demand with requestAnimationFrame
 * coalescing, forwarding pointer, wheel and drop events to the MapController.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../api';
import { drawScene } from '../../render/renderer';
import { themePalette, type Palette } from '../../render/palette';
import { groundHeight } from '../../render/terrain';
import { gridRef } from '../../render/view';
import { useEditor, type EditorStore } from '../../store/editor';
import { MapController } from '../../tools/controller';
import { TRAY_MIME } from './panels/UnplacedTray';

/** The map canvas with its zoom buttons and status line. */
export function MapCanvas({ store, controllerRef }: { store: EditorStore; controllerRef: React.MutableRefObject<MapController | null> }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctl = useMemo(() => new MapController(store), [store]);
  controllerRef.current = ctl;
  // handy when checking the editor by hand in development
  if (import.meta.env.DEV) (window as unknown as { __zooEditor?: unknown }).__zooEditor = { store, ctl };
  const [status, setStatus] = useState({ hint: '', where: '' });
  const message = useEditor(store, (s) => s.message);
  const tool = useEditor(store, (s) => s.tool);
  const overlayFile = useEditor(store, (s) => s.doc.overlay?.file ?? null);
  const overlayImg = useRef<{ file: string; img: HTMLImageElement | null } | null>(null);
  const palette = useRef<Palette>(themePalette());
  const frame = useRef(0);

  // the screenshot under the map
  useEffect(() => {
    if (!overlayFile) { overlayImg.current = null; return; }
    const entry: { file: string; img: HTMLImageElement | null } = { file: overlayFile, img: null };
    overlayImg.current = entry;
    const img = new Image();
    img.onload = () => { entry.img = img; request(); };
    img.src = api.overlayUrl(store.planId, overlayFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayFile, store]);

  const draw = () => {
    frame.current = 0;
    const cv = canvasRef.current, wrap = wrapRef.current;
    if (!cv || !wrap) return;
    const w = wrap.clientWidth, h = wrap.clientHeight, dpr = window.devicePixelRatio || 1;
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      cv.style.width = `${w}px`;
      cv.style.height = `${h}px`;
    }
    ctl.resize(w, h);
    const g = cv.getContext('2d');
    if (!g) return;
    const s = store.getState();
    drawScene(g, ctl.transform(), dpr, {
      doc: s.doc,
      derived: s.derived,
      analysis: s.analysis,
      species: speciesMap(s.species),
      palette: palette.current,
      selection: s.selection,
      handles: s.tool === 'select',
      grips: s.advanced && s.tool === 'select',
      showGrid: s.showGrid,
      overlayImage: overlayImg.current?.img ?? null,
      preview: ctl.preview(),
      zoom: s.view.zoom,
      scaleBarLift: 34,
    });
  };
  const request = () => {
    if (!frame.current) frame.current = requestAnimationFrame(draw);
  };

  const updateStatus = () => {
    const s = store.getState(), p = ctl.pointer;
    let where = '';
    if (p && p[0] >= 0 && p[1] >= 0 && p[0] <= s.doc.size && p[1] <= s.doc.size) {
      const hgt = groundHeight(s.doc, p[0], p[1]);
      where = `${gridRef(s.doc.size, p[0], p[1])} · ${Math.round(p[0])} m, ${Math.round(p[1])} m${hgt !== null ? ` · ground ${hgt.toFixed(1)} m` : ''}`;
    }
    const hint = ctl.hint();
    setStatus((old) => (old.hint === hint && old.where === where ? old : { hint, where }));
  };

  useEffect(() => {
    ctl.onChange = () => { request(); updateStatus(); };
    const unsub = store.subscribe(() => { request(); updateStatus(); });
    const ro = new ResizeObserver(request);
    if (wrapRef.current) ro.observe(wrapRef.current);
    // follow the light/dark switch
    const mo = new MutationObserver(() => { palette.current = themePalette(); request(); });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onScheme = () => { palette.current = themePalette(); request(); };
    mq.addEventListener('change', onScheme);
    request();
    updateStatus();
    return () => {
      unsub();
      ro.disconnect();
      mo.disconnect();
      mq.removeEventListener('change', onScheme);
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, ctl]);

  // native listeners: wheel must not scroll the page; pointer events need offsetX on the canvas
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const down = (e: PointerEvent) => ctl.pointerDown(e, cv);
    const move = (e: PointerEvent) => ctl.pointerMove(e);
    const up = () => ctl.pointerUp();
    const dbl = (e: MouseEvent) => ctl.doubleClick(e);
    const wheel = (e: WheelEvent) => ctl.wheel(e);
    const leave = () => ctl.pointerLeave();
    const menu = (e: Event) => e.preventDefault();
    cv.addEventListener('pointerdown', down);
    cv.addEventListener('pointermove', move);
    cv.addEventListener('pointerup', up);
    cv.addEventListener('pointercancel', up);
    cv.addEventListener('pointerleave', leave);
    cv.addEventListener('dblclick', dbl);
    cv.addEventListener('wheel', wheel, { passive: false });
    cv.addEventListener('contextmenu', menu);
    return () => {
      cv.removeEventListener('pointerdown', down);
      cv.removeEventListener('pointermove', move);
      cv.removeEventListener('pointerup', up);
      cv.removeEventListener('pointercancel', up);
      cv.removeEventListener('pointerleave', leave);
      cv.removeEventListener('dblclick', dbl);
      cv.removeEventListener('wheel', wheel);
      cv.removeEventListener('contextmenu', menu);
    };
  }, [ctl]);

  // a new tool throws away a half-drawn shape
  useEffect(() => { ctl.resetTool(); }, [tool, ctl]);

  const cursor = tool === 'select' ? 'default' : tool === 'delete' ? 'not-allowed' : 'crosshair';

  return (
    <div
      className="map-wrap"
      ref={wrapRef}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(TRAY_MIME)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
      onDrop={(e) => {
        const raw = e.dataTransfer.getData(TRAY_MIME);
        if (!raw) return;
        e.preventDefault();
        const r = canvasRef.current?.getBoundingClientRect();
        if (!r) return;
        try { ctl.dropHabitat(e.clientX - r.left, e.clientY - r.top, JSON.parse(raw)); } catch { /* not ours */ }
      }}
    >
      <canvas ref={canvasRef} tabIndex={0} aria-label="Zoo map" style={{ cursor }} />
      <div className="map-zoom">
        <button type="button" title="Zoom in (+)" onClick={() => ctl.zoomBy(1.4)}>＋</button>
        <button type="button" title="Zoom out (−)" onClick={() => ctl.zoomBy(1 / 1.4)}>－</button>
        <button type="button" title="Show the whole zoo (0)" onClick={() => store.fit()}>Fit</button>
      </div>
      <div className="map-hint">
        <span>{message || status.hint}</span>
        {status.where && <span>{status.where}</span>}
      </div>
    </div>
  );
}

let lastSpecies: unknown = null;
let lastMap: Map<string, import('../../../shared/types').Species> | null = null;
function speciesMap(list: import('../../../shared/types').Species[] | null) {
  if (!list) return null;
  if (list !== lastSpecies) { lastSpecies = list; lastMap = new Map(list.map((s) => [s.id, s])); }
  return lastMap;
}
